import {
  PropertyGroupSplitBy,
  Segment
} from "views/property-groups-configuration/types";
import { SegmentCard } from "./SegmentCard";
import { useCallback, useRef, useEffect } from "react";

export const SegmentsList = ({
  segments,
  setSegments,
  segmentOptions,
  currentStep,
  propertyTotalCount,
  validationErrors = [],
  isLastStep = false,
  clearValidationError,
  othersName = "",
  setOthersName
}: {
  segments: Segment[];
  setSegments: (segments: Segment[]) => void;
  segmentOptions: any[];
  currentStep: PropertyGroupSplitBy;
  propertyTotalCount: number;
  validationErrors?: {
    name: boolean;
    options: boolean;
    duplicateOptions?: string[];
    duplicateName?: boolean;
  }[];
  isLastStep?: boolean;
  clearValidationError?: (
    segmentIndex: number,
    fieldType: "name" | "options"
  ) => void;
  othersName?: string;
  setOthersName?: (name: string) => void;
}) => {
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevSegmentsLength = useRef<number>(segments.length);

  useEffect(() => {
    if (segments.length > prevSegmentsLength.current) {
      const newSegmentIndex = segments.length - 1;
      const newSegmentRef = segmentRefs.current[newSegmentIndex];

      if (newSegmentRef)
        newSegmentRef.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    prevSegmentsLength.current = segments.length;
  }, [segments.length]);

  const handleSegmentChange = useCallback(
    (index: number, segment: Segment) => {
      const newSegments = [...segments];
      newSegments[index] = segment;
      setSegments(newSegments);
    },
    [segments, setSegments]
  );

  const handleRemoveSegment = useCallback(
    (index: number) => {
      const newSegments = [...segments];
      newSegments.splice(index, 1);
      setSegments(newSegments);
    },
    [segments, setSegments]
  );

  const handleOthersNameChange = useCallback(
    (segment: Segment) => {
      if (setOthersName) setOthersName(segment.name);
    },
    [setOthersName]
  );

  useEffect(() => {
    segmentRefs.current = segmentRefs.current.slice(0, segments.length + 1);
  }, [segments.length]);

  return (
    <div className="space-y-6">
      {segments.map((segment, index) => (
        <div
          key={`segment-container-${index}`}
          ref={(el) => (segmentRefs.current[index] = el)}
        >
          <SegmentCard
            propertyTotalCount={propertyTotalCount}
            segmentIndex={index}
            currentStep={currentStep}
            segmentOptions={segmentOptions}
            title={isLastStep ? `Group ${index + 1}` : `Segment ${index + 1}`}
            key={`segment-${index}`}
            segment={segment}
            allSegments={segments}
            onSegmentChange={(segment) => handleSegmentChange(index, segment)}
            onRemove={() => handleRemoveSegment(index)}
            errors={validationErrors[index]}
            isLastStep={isLastStep}
            clearValidationError={
              clearValidationError
                ? (fieldType: "name" | "options") =>
                    clearValidationError(index, fieldType)
                : undefined
            }
          />
        </div>
      ))}

      <div ref={(el) => (segmentRefs.current[segments.length] = el)}>
        <SegmentCard
          propertyTotalCount={propertyTotalCount}
          segmentIndex={segments.length}
          currentStep={currentStep}
          segmentOptions={segmentOptions}
          title={
            isLastStep
              ? `Group ${segments.length + 1}`
              : `Segment ${segments.length + 1}`
          }
          key={`segment-${segments.length}`}
          segment={{
            name: othersName,
            options: [],
            isDisabled: true,
            isOthersSegment: true
          }}
          allSegments={segments}
          onSegmentChange={handleOthersNameChange}
          onRemove={() => undefined}
          errors={validationErrors[segments.length]}
          isLastStep={isLastStep}
          clearValidationError={
            clearValidationError
              ? (fieldType: "name" | "options") =>
                  clearValidationError(segments.length, fieldType)
              : undefined
          }
        />
      </div>
    </div>
  );
};
